import { createCrudStore } from "@/store/crudStore";
import { apiService } from "@/services/api";

const base = createCrudStore("forrajes");

const forrajesStore = {
  ...base,

  state: {
    ...base.state,
    // Cálculos y estadísticas
    estadisticasForrajeras: null,
    costoPorResultado: null,
    produccionCarne: null,
    produccionLeche: null,
    estacionActual: null,
    coeficientesEV: null,
  },

  getters: {
    ...base.getters,
    forrajes(state) {
      return Array.isArray(state.forrajes) ? state.forrajes : [];
    },
    forrajesActivos(state) {
      return (state.forrajes || []).filter((f) => f.activo !== false);
    },
    forrajesPredefinidos(state) {
      return (state.forrajes || []).filter((f) => f.es_predefinido);
    },
    forrajesPerennes(state) {
      return (state.forrajes || []).filter((f) => f.es_perenne);
    },
    estadisticasForrajeras: (state) => state.estadisticasForrajeras,
    costoPorResultado: (state) => state.costoPorResultado,
    produccionCarne: (state) => state.produccionCarne,
    produccionLeche: (state) => state.produccionLeche,
    estacionActual: (state) => state.estacionActual,
    coeficientesEV: (state) => state.coeficientesEV,
  },

  mutations: {
    ...base.mutations,
    setEstadisticasForrajeras: (state, data) =>
      (state.estadisticasForrajeras = data),
    setCostoPorResultado: (state, data) => (state.costoPorResultado = data),
    setProduccionCarne: (state, data) => (state.produccionCarne = data),
    setProduccionLeche: (state, data) => (state.produccionLeche = data),
    setEstacionActual: (state, data) => (state.estacionActual = data),
    setCoeficientesEV: (state, data) => (state.coeficientesEV = data),
    clearState(state) {
      state.forrajes = [];
      state.estadisticasForrajeras = null;
      state.costoPorResultado = null;
      state.produccionCarne = null;
      state.produccionLeche = null;
      state.estacionActual = null;
      state.coeficientesEV = null;
      state.loading = false;
      state.error = null;
    },
  },

  actions: {
    ...base.actions,

    // ── CRUD ─────────────────────────────────────────
    getForrajes: base.actions.getAllStates,

    async postForraje({ dispatch }, payload) {
      return dispatch("executeApiCall", {
        apiCallFunction: () => apiService.create("forrajes", payload),
        onSuccessCallback: (commit, data) => commit("addItem", data),
        errorMessage: "Error al crear forraje",
      });
    },

    async patchForraje({ dispatch }, payload) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.update("forrajes", payload.id, payload),
        onSuccessCallback: (commit, data) => commit("updateItem", data),
        errorMessage: "Error al editar forraje",
      });
    },

    async deleteForraje({ dispatch }, id) {
      return dispatch("executeApiCall", {
        apiCallFunction: () => apiService.delete("forrajes", id),
        onSuccessCallback: (commit) => commit("removeItem", id),
        errorMessage: "Error al eliminar forraje",
      });
    },

    // ── SEED ─────────────────────────────────────────
    async seedForrajes({ dispatch }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () => apiService.postCustom("/forrajes/seed", {}),
        onSuccessCallback: async () => {
          await dispatch("getForrajes");
        },
        errorMessage: "Error al crear forrajes predefinidos",
      });
    },

    // ── PARÁMETROS ESTACIONALES ─────────────────────
    async putEstaciones({ dispatch }, { forrajeId, parametros }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.putCustom(`/forrajes/${forrajeId}/estaciones`, parametros),
        onSuccessCallback: async () => {
          await dispatch("getForrajes");
        },
        errorMessage: "Error al actualizar parámetros estacionales",
      });
    },

    // ── COSTOS DE SIEMBRA ───────────────────────────
    async getCostosSiembra({ dispatch }, siembraId) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(`forrajes/costos-siembra/${siembraId}`),
        onSuccessCallback: () => {},
        errorMessage: "Error al cargar costos de siembra",
      });
    },

    async postCostoSiembra({ dispatch }, payload) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom("/forrajes/costos-siembra", payload),
        onSuccessCallback: () => {},
        errorMessage: "Error al registrar costo de siembra",
      });
    },

    async getResumenCostoSiembra(_, { siembraId, monedaBase = "peso" }) {
      const res = await apiService.getCustom(
        `forrajes/costos-siembra/${siembraId}/resumen?moneda_base=${monedaBase}`
      );
      return res.data;
    },

    // ── CÁLCULOS ────────────────────────────────────
    async fetchEstacionActual({ commit }) {
      try {
        const res = await apiService.getCustom(
          "forrajes/calculos/estacion-actual"
        );
        commit("setEstacionActual", res.data);
        return res.data;
      } catch (e) {
        console.error("Error cargando estación:", e);
      }
    },

    async fetchCoeficientesEV({ commit }) {
      try {
        const res = await apiService.getCustom(
          "forrajes/calculos/coeficientes-ev"
        );
        commit("setCoeficientesEV", res.data);
        return res.data;
      } catch (e) {
        console.error("Error cargando coeficientes EV:", e);
      }
    },

    async fetchProduccionCarne({ commit }, params = {}) {
      try {
        const qs = new URLSearchParams();
        if (params.fecha_desde) qs.append("fecha_desde", params.fecha_desde);
        if (params.fecha_hasta) qs.append("fecha_hasta", params.fecha_hasta);
        if (params.hectareas) qs.append("hectareas", params.hectareas);
        const url = `forrajes/calculos/produccion-carne${
          qs.toString() ? "?" + qs.toString() : ""
        }`;
        const res = await apiService.getCustom(url);
        commit("setProduccionCarne", res.data);
        return res.data;
      } catch (e) {
        console.error("Error cargando producción carne:", e);
      }
    },

    async fetchProduccionLeche({ commit }, params = {}) {
      try {
        const qs = new URLSearchParams();
        if (params.fecha_desde) qs.append("fecha_desde", params.fecha_desde);
        if (params.fecha_hasta) qs.append("fecha_hasta", params.fecha_hasta);
        if (params.hectareas) qs.append("hectareas", params.hectareas);
        const url = `forrajes/calculos/produccion-leche${
          qs.toString() ? "?" + qs.toString() : ""
        }`;
        const res = await apiService.getCustom(url);
        commit("setProduccionLeche", res.data);
        return res.data;
      } catch (e) {
        console.error("Error cargando producción leche:", e);
      }
    },

    async fetchCostoPorResultado({ commit }, params = {}) {
      try {
        const qs = new URLSearchParams();
        if (params.modo_produccion)
          qs.append("modo_produccion", params.modo_produccion);
        if (params.fecha_desde) qs.append("fecha_desde", params.fecha_desde);
        if (params.fecha_hasta) qs.append("fecha_hasta", params.fecha_hasta);
        if (params.hectareas) qs.append("hectareas", params.hectareas);
        const url = `forrajes/calculos/costo-por-resultado${
          qs.toString() ? "?" + qs.toString() : ""
        }`;
        const res = await apiService.getCustom(url);
        commit("setCostoPorResultado", res.data);
        return res.data;
      } catch (e) {
        console.error("Error cargando costo por resultado:", e);
      }
    },

    async fetchEstadisticasForrajeras({ commit }, params = {}) {
      try {
        const qs = params.anio ? `?anio=${params.anio}` : "";
        const res = await apiService.getCustom(
          `forrajes/calculos/estadisticas${qs}`
        );
        commit("setEstadisticasForrajeras", res.data);
        return res.data;
      } catch (e) {
        console.error("Error cargando estadísticas forrajeras:", e);
      }
    },
  },
};

export default forrajesStore;
